import {
  Badge,
  PageHeader,
  Stack,
  ViewHeader,
  ViewItemField,
  ViewItemFieldWithChildren,
} from '@fukuri-admins/ui/common';
import { getClientEditLink } from '~/utils/getLinks';
import styles from './styles.module.scss';

/**
 * フィーチャーフラグの型定義
 */
type ClientFeatureFlag = {
  /** フィーチャーフラグのキー */
  featureKey: string;
  /** フィーチャーフラグの値 */
  isEnabled: boolean;
  /** フィーチャーフラグの表示用ラベル */
  label: string;
};

/**
 * カスタムフィールドの型定義
 */
type CustomField = {
  /** カスタムフィールド定義ID（React key用） */
  id: number;
  /** ラベル */
  label: string;
  /** 表示値 */
  value: string;
};

/**
 * クライアント詳細情報の型定義
 */
type LegacyClientDetail = {
  /** クライアントID */
  id: number;
  /** 会社名 */
  clientName: string;
  /** フィーチャーフラグ一覧（未指定時は空配列として扱う想定） */
  clientFeatureFlags?: ClientFeatureFlag[];
  /** カスタムフィールド一覧 */
  customFields: CustomField[];
  /** 担当者 */
  contactPersonName: string;
  /** メールアドレス */
  contactPersonEmail: string;
  /** 電話番号 */
  contactPersonPhone: string;
  /** 法人番号 */
  corporateNumber: string;
  /** 外部企業ID */
  externalClientId: string;
  /** グループ名 */
  groupName: string;
  /** ギフトプラン（フォーマット済み） */
  giftPlan: string;
  /** 利用予定者数（フォーマット済み） */
  employeeCount: string;
  /** 利用者数（フォーマット済み） */
  userCount: string;
  /** 課金対象人数（フォーマット済み） */
  nonInvitedUserCount: string;
  /** 作成日時（フォーマット済み） */
  createdAt: string;
  /** 更新日時（フォーマット済み） */
  updatedAt: string;
};

type Props = {
  clientDetail: LegacyClientDetail;
};

/**
 * クライアント詳細のPresentationコンポーネント
 */
export const LegacyClientDetailPresentation: React.FC<Props> = ({
  clientDetail: {
    id,
    clientName,
    customFields,
    contactPersonName,
    contactPersonEmail,
    contactPersonPhone,
    corporateNumber,
    externalClientId,
    groupName,
    giftPlan,
    employeeCount,
    userCount,
    nonInvitedUserCount,
    createdAt,
    updatedAt,
    clientFeatureFlags,
  },
}) => (
  <>
    <PageHeader
      variant='breadcrumb'
      breadcrumbItems={[{ label: '利用企業', href: '/clients' }, { label: clientName }]}
    />
    <div className={styles.content}>
      <ViewHeader title={clientName} editLink={getClientEditLink(String(id))} />
      <Stack direction='column' gap={24}>
        <ViewItemField label='会社名' direction='row' value={clientName} />
        <ViewItemField label='担当者' direction='row' value={contactPersonName} />
        <ViewItemField label='メールアドレス' direction='row' value={contactPersonEmail} />
        <ViewItemField label='電話番号' direction='row' value={contactPersonPhone} />
        <ViewItemField label='法人番号' direction='row' value={corporateNumber} />
        <ViewItemField label='外部企業ID' direction='row' value={externalClientId} />
        <ViewItemField label='グループ' direction='row' value={groupName} />
        <ViewItemField label='ギフトプラン' direction='row' value={giftPlan} />
        <ViewItemField label='利用予定者数' direction='row' value={employeeCount} />
        <ViewItemField label='利用者数' direction='row' value={userCount} />
        <ViewItemField label='課金対象人数' direction='row' value={nonInvitedUserCount} />
        {customFields.map(field => (
          <ViewItemField key={field.id} label={field.label} direction='row' value={field.value} />
        ))}
        {clientFeatureFlags?.map(flag => (
          <ViewItemFieldWithChildren key={flag.featureKey} label={flag.label} direction='row'>
            <Badge
              size='md'
              variant='subtle'
              status={flag.isEnabled ? 'success' : 'neutral'}
              label={flag.isEnabled ? '有効' : '無効'}
              showClose={false}
            />
          </ViewItemFieldWithChildren>
        ))}
        <ViewItemField label='作成日時' direction='row' value={createdAt} />
        <ViewItemField label='更新日時' direction='row' value={updatedAt} />
      </Stack>
    </div>
  </>
);
